/**
 * ================================================================
 * FirstCut - Content Focus Modal (3-Step Simplified Flow)
 * ================================================================
 * BEFORE MODIFYING: Read /app/memory/CONVENTIONS.md
 *
 * FLOW:
 * 1. Photo - Upload and analyze product/space photo
 * 2. Direction - Select AI-generated creative direction (with scores)
 * 3. Generate - Configure platforms and generate content
 *
 * KEY CONVENTIONS:
 * - Uses V2 API endpoints (/v2/focus/generate-options)
 * - photography_directions (array) with: title, what_to_capture, timing, mood, technique
 * - focusOptions (not just "options"), photoAnalysis (not just "analysis")
 * - Event handlers: handleX for internal, onX for props
 * ================================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../services/api';
import { FiX, FiLoader, FiUpload, FiCheck, FiChevronDown, FiChevronUp, FiImage, FiVideo, FiFileText, FiZap, FiDollarSign, FiPackage, FiSkipForward, FiInfo, FiCamera, FiPlus } from 'react-icons/fi';
import PRICING_CONFIG from '../config/pricing';
import { QualityScoreBadge, QualityGateIndicator } from './QualityScoreBadge';
import GenerationProgress from './GenerationProgress';

// Credit costs from centralized config
const CREDIT_COSTS = PRICING_CONFIG.CREDIT_COSTS;

const IMAGE_RATIOS = [
  { id: '1:1', label: 'Square', desc: 'Posts' },
  { id: '4:5', label: 'Portrait', desc: 'Feed' },
  { id: '9:16', label: 'Vertical', desc: 'Reels/Stories' },
  { id: '16:9', label: 'Landscape', desc: 'YouTube' }
];

const IMAGE_QUALITY = [
  { id: 'draft', label: 'Draft', desc: 'Quick preview' },
  { id: 'standard', label: 'Standard', desc: 'Good quality' },
  { id: 'high', label: 'High', desc: 'Best quality' }
];

function ContentFocusModal({ campaignId, campaign, client, onClose, onSuccess, preloadedFocus }) {
  // DEBUG: Log props on mount to help identify any undefined values
  console.log('ContentFocusModal props:', { 
    campaignId, 
    campaign: campaign ? 'defined' : 'undefined', 
    client: client ? 'defined' : 'undefined',
    preloadedFocus: preloadedFocus ? 'defined' : 'undefined'
  });
  
  // SIMPLIFIED 3-STEP FLOW: 1=Photo, 2=Direction (with scores), 3=Generate
  // If preloadedFocus is provided (Fresh Angle flow), start at Step 2 with photo pre-loaded
  const [step, setStep] = useState(preloadedFocus ? 2 : 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0); // Track retry attempts for progress indicator
  const [isAIBusy, setIsAIBusy] = useState(false); // Track if AI is experiencing issues
  const contentRef = useRef(null);
  const [userCredits, setUserCredits] = useState({ 
    used: 0, 
    limit: PRICING_CONFIG.FREE_CREDITS, 
    remaining: PRICING_CONFIG.FREE_CREDITS,
    tier: 'free',
    creditCosts: CREDIT_COSTS 
  });
  
  // Step 1: Photo + Context Mode (NEW GUIDED CONTEXT SYSTEM)
  // Pre-load from focus if provided (Fresh Angle flow)
  const [uploadedPhoto, setUploadedPhoto] = useState(preloadedFocus?.source_image_url ? {
    base64: null,
    preview: preloadedFocus.source_image_url,
    name: 'preloaded',
    url: preloadedFocus.source_image_url
  } : null);
  const [contextMode, setContextMode] = useState(preloadedFocus?.context_mode || null);
  const [photoAnalysis, setPhotoAnalysis] = useState(preloadedFocus?.photo_context || null);
  const [analyzingPhoto, setAnalyzingPhoto] = useState(false);
  const [recentPhotos, setRecentPhotos] = useState([]); // Recently uploaded photos for this client
  const [loadingRecentPhotos, setLoadingRecentPhotos] = useState(false);
  const photoFileInputRef = useRef(null);
  
  // Context mode definitions with detailed focus and coverage info
  const CONTEXT_MODES = [
    { 
      id: 'product', 
      icon: '📦', 
      label: 'Product', 
      tagline: 'The object is the star',
      focus: ['Material & texture', 'Design details', 'Features & functions', 'Color & finish'],
      covers: ['Electronics', 'Furniture', 'Accessories', 'Food items', 'Apparel', 'Packaging'],
      aiIgnores: 'Background, staging, surroundings'
    },
    { 
      id: 'space', 
      icon: '🏠', 
      label: 'Space', 
      tagline: 'The environment sets the mood',
      focus: ['Atmosphere & vibe', 'Layout & flow', 'Lighting quality', 'Design style'],
      covers: ['Store interiors', 'Restaurants', 'Hotels', 'Showrooms', 'Homes', 'Venues'],
      aiIgnores: 'Individual products, people, removable items'
    }
  ];
  
  // Step 2: Focus/Direction selection (with scoring)
  const [focusOptions, setFocusOptions] = useState([]);
  const [focusQualityGate, setFocusQualityGate] = useState(null); // Quality gate info
  const [selectedOption, setSelectedOption] = useState(null);
  const [expandedOption, setExpandedOption] = useState(0);
  const [focusGenerated, setFocusGenerated] = useState(false);
  
  // Custom focus option
  const [useCustom, setUseCustom] = useState(false);
  const [customBrief, setCustomBrief] = useState('');
  const [generatingCustom, setGeneratingCustom] = useState(false);
  const [referenceImages, setReferenceImages] = useState([]);

  // Step 1: What to highlight (optional - influences creative directions)
  const [highlightText, setHighlightText] = useState('');
  
  // Step 3: Content generation options
  const [additionalBrief, setAdditionalBrief] = useState('');
  const [imageRatio, setImageRatio] = useState('1:1');
  const [contentStyle, setContentStyle] = useState('clean'); // "clean", "spotlight", "announcement"
  
  // Text Overlay Intelligence - optional user-provided overlay text
  const [overlayPrimary, setOverlayPrimary] = useState(''); // Product name (e.g., "DIRTY COFFEE")
  const [overlaySecondary, setOverlaySecondary] = useState(''); // Description (e.g., "A thrilling sensation...")
  const [overlayTertiary, setOverlayTertiary] = useState(''); // Tagline (e.g., "Limited release, all May")
  const [suggestingOverlay, setSuggestingOverlay] = useState(false); // Loading state for AI suggestions
  const [renderTextOnImage, setRenderTextOnImage] = useState(false); // Toggle: render text directly on image via Nano Banana
  
  // Brand type detection feedback
  const [brandTypeMismatch, setBrandTypeMismatch] = useState({ show: false, message: '', detected: null, confidence: 0 });
  const [imageQuality, setImageQuality] = useState('standard');
  const [generateIndividualImages, setGenerateIndividualImages] = useState(true);
  const [imagesPerIdea, setImagesPerIdea] = useState(null); // Will be set based on detected angles
  const [selectedPlatforms, setSelectedPlatforms] = useState({
    instagram: { enabled: true, types: ['post'] },
    facebook: { enabled: false, types: [] },
    youtube: { enabled: false, types: [] }
  });

  const platformOptions = {
    instagram: ['post', 'story', 'reel', 'carousel'],
    facebook: ['post', 'story', 'video'],
    youtube: ['short', 'long', 'community']
  };
  
  // Only 'post' is available for now, rest are coming soon
  const enabledContentTypes = ['post'];

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    fetchUserCredits();
  }, []);

  // Scroll content to top when step changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [step]);
  
  // Reset CAMPAIGN-SPECIFIC state when campaign changes
  // NOTE: Client context (client prop, recentPhotos) is preserved - only campaign data is cleared
  useEffect(() => {
    // Reset to step 1
    setStep(1);
    setError('');
    
    // Reset photo/context state for THIS campaign
    // (recentPhotos are client-level, not cleared here)
    setUploadedPhoto(null);
    setContextMode(null);
    setPhotoAnalysis(null);
    setAnalyzingPhoto(false);
    
    // Reset focus state (campaign-specific)
    setFocusOptions([]);
    setSelectedOption(null);
    setExpandedOption(0);
    setFocusGenerated(false);
    
    // Reset custom focus
    setUseCustom(false);
    setCustomBrief('');
    setGeneratingCustom(false);
    setReferenceImages([]);
    
    // Reset generation options to defaults
    setAdditionalBrief('');
    setHighlightText('');
    setImageRatio('1:1');
    setContentStyle('clean'); // Reset content style
    setOverlayPrimary(''); // Reset overlay text
    setOverlaySecondary('');
    setOverlayTertiary('');
    setRenderTextOnImage(false); // Reset text on image toggle
    setImageQuality('standard');
    setGenerateIndividualImages(true);
    setImagesPerIdea(null); // Will be set based on detected angles
    setSelectedPlatforms({
      instagram: { enabled: true, types: ['post'] },
      facebook: { enabled: false, types: [] },
      youtube: { enabled: false, types: [] }
    });
    
    console.log('Campaign changed, campaign-specific state reset. Client context preserved.');
    console.log('Campaign:', campaignId, '| Client:', client?.name);
  }, [campaignId, client?.name]);
  
  // Load recent photos when client changes (client-level data, persists across campaigns)
  useEffect(() => {
    if (client?.id) {
      loadRecentPhotos();
    }
  }, [client?.id]);
  
  // Load recent photos for this client
  const loadRecentPhotos = async () => {
    if (!client?.id) return;
    setLoadingRecentPhotos(true);
    try {
      const response = await api.getClientRecentPhotos(client.id);
      setRecentPhotos(response.photos || []);
    } catch (err) {
      console.error('Error loading recent photos:', err);
      // Not critical, just log and continue
    } finally {
      setLoadingRecentPhotos(false);
    }
  };
  
  // Select a recent photo
  const selectRecentPhoto = (photo) => {
    setUploadedPhoto({
      base64: photo.url, // URL from storage
      preview: photo.url,
      name: photo.name || 'Recent photo',
      isFromRecent: true,
      recentPhotoId: photo.id
    });
    // Also restore the context mode if saved
    if (photo.context_mode) {
      setContextMode(photo.context_mode);
    }
    // Reset analysis since it's a different photo now
    setPhotoAnalysis(null);
  };

  // Handle photo upload
  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (JPG, PNG, etc.)');
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be under 10MB');
      return;
    }
    
    setError('');
    
    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedPhoto({
        base64: e.target.result,
        preview: e.target.result,
        name: file.name
      });
      // Reset analysis when new photo is uploaded
      setPhotoAnalysis(null);
    };
    reader.readAsDataURL(file);
  };
  
  // Analyze photo with selected context mode
  const analyzePhoto = async () => {
    if (!uploadedPhoto || !contextMode) {
      setError('Please upload a photo and select a context mode');
      return;
    }
    
    setAnalyzingPhoto(true);
    setError('');
    
    // Clear any previous analysis first
    setPhotoAnalysis(null);
    
    try {
      console.log('🔍 V2 Analyzing photo with context mode:', contextMode);
      console.log('Photo base64 length:', uploadedPhoto.base64?.length);
      
      // Use V2 endpoint with literal constraint system + Visual DNA
      const response = await api.analyzePhotoV2({
        image_base64: uploadedPhoto.base64,
        context_mode: contextMode,
        client_id: client?.id,
        campaign_id: campaignId,
        extract_visual_dna: true  // Request Visual DNA extraction
      });
      
      console.log('V2 Analysis response:', response);
      
      if (response.success && response.analysis) {
        // V2 format with literal constraints + Visual DNA
        const analysisWithMode = {
          ...response.analysis,
          _context_mode: contextMode,
          _analyzed_at: new Date().toISOString(),
          _version: 'v2',
          // V2 specific fields
          visible_elements: response.analysis.visible_elements || [],
          not_visible: response.analysis.not_visible || [],
          literal_description: response.analysis.literal_description,
          constraint: response.constraint,
          // Scene blueprint for space mode
          scene_blueprint: response.scene_blueprint,
          // NEW: Visual DNA for angle enforcement + demographic preservation
          visual_dna: response.visual_dna,
          source_image_url: uploadedPhoto?.base64,
          has_virtual_rephotography: !!response.scene_blueprint,
          has_visual_dna: !!response.visual_dna
        };
        setPhotoAnalysis(analysisWithMode);
        
        // Log V2 analysis results
        console.log('✅ V2 Photo analysis stored:');
        console.log('  Primary subject:', response.analysis.primary_subject);
        console.log('  Is exterior:', response.analysis.is_exterior);
        console.log('  Visible elements:', response.analysis.visible_elements?.length || 0);
        console.log('  Not visible (constraints):', response.analysis.not_visible?.length || 0);
        
        if (response.scene_blueprint) {
          console.log('✅ Scene Blueprint created for Virtual Re-Photography');
        }
        
        // Log Visual DNA info
        if (response.visual_dna) {
          console.log('✅ Visual DNA extracted:');
          console.log('  Space type:', response.visual_dna.space_type);
          console.log('  Available angles:', response.visual_dna.available_angles?.length || 0);
          console.log('  People present:', response.visual_dna.people?.present || false);
          console.log('  Cultural context:', response.visual_dna.cultural_context?.region || 'unknown');
        }
        
        // Handle brand type detection feedback
        if (response.brand_type_detection) {
          const detection = response.brand_type_detection;
          console.log('📊 Brand type detection:', detection);
          
          if (detection.mismatch && detection.mismatch_message) {
            // Show mismatch notification (non-blocking)
            setBrandTypeMismatch({
              show: true,
              message: detection.mismatch_message,
              detected: detection.detected_type,
              confidence: detection.confidence
            });
          }
        }
      } else {
        setError('Failed to analyze photo. Please try again.');
      }
    } catch (err) {
      console.error('V2 Photo analysis error:', err);
      // Handle 402 (insufficient credits) with a user-friendly message
      if (err.response?.status === 402 || err.response?.data?.error === 'Insufficient credits') {
        const detail = err.response?.data?.detail || {};
        setError(`Not enough credits to analyze photo. You need ${detail.credits_needed || 2} credits but have ${detail.credits_available || 0}. Please purchase more credits to continue.`);
      } else {
        setError(err.message || 'Failed to analyze photo. Please try again.');
      }
    } finally {
      setAnalyzingPhoto(false);
    }
  };
  
  // Remove uploaded photo
  const removePhoto = () => {
    setUploadedPhoto(null);
    setPhotoAnalysis(null);
    setContextMode(null);
  };

  const fetchUserCredits = async () => {
    try {
      const usage = await api.getUserUsage();
      // Use the new credits-based response with config fallbacks
      const credits = usage.credits || {
        used: usage.usage?.credits_used || 0,
        limit: usage.limits?.credits_per_month || PRICING_CONFIG.FREE_CREDITS,
        remaining: 0
      };
      credits.remaining = credits.remaining || Math.max(0, credits.limit - credits.used);
      
      setUserCredits({
        used: credits.used,
        limit: credits.limit,
        remaining: credits.remaining,
        tier: usage.tier || 'free',
        creditCosts: CREDIT_COSTS
      });
    } catch (err) {
      console.error('Error fetching user credits:', err);
    }
  };

  // Generate focus options - NOW with photo analysis context
  const generateFocusOptions = async (isRegeneration = false) => {
    if (!campaignId) {
      setError('Campaign ID is missing. Please try again.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    // Store previous options for passing to backend (for learning/avoiding repeats)
    const previousOptions = isRegeneration ? [...focusOptions] : [];
    
    if (!isRegeneration) {
      setFocusGenerated(false);
    }
    
    try {
      // Check if we have photo analysis - if not, use no-photo endpoint
      if (!photoAnalysis || !photoAnalysis.primary_subject) {
        console.log('🔍 Using NO-PHOTO Focus Generator (client + campaign context only)');
        
        const response = await api.generateFocusOptionsNoPhoto({
          client_id: client?.id,
          campaign_id: campaignId,
          num_options: 3,
          avoid_options: previousOptions.map(o => o.name)
        });
        
        console.log('No-photo focus response:', response);
        
        if (response.success && response.options?.length > 0) {
          console.log('✅ Setting focus options (no-photo mode):', response.options);
          setFocusOptions(response.options);
          setFocusQualityGate(response.quality_gate || null); // Store quality gate
          setExpandedOption(0);
          setFocusGenerated(true);
        } else {
          setError('Failed to generate focus options. Please try again.');
        }
        return;
      }
      
      // ALWAYS use V2 Focus Generator now - legacy path removed
      console.log('🔍 Using V2 Focus Generator with literal constraints');
      console.log('photoAnalysis state:', photoAnalysis);
      console.log('Photo analysis version:', photoAnalysis?._version);
      console.log('Is exterior:', photoAnalysis?.is_exterior);
      console.log('Shot type:', photoAnalysis?.shot_type);
      console.log('Primary subject:', photoAnalysis?.primary_subject);
      console.log('Visible elements count:', photoAnalysis?.visible_elements?.length);
      
      // Build V2 photo context from any analysis format
      const v2PhotoContext = {
        context_mode: contextMode,
        analysis: {
          primary_subject: photoAnalysis.primary_subject || '',
          literal_description: photoAnalysis.literal_description || '',
          visible_elements: photoAnalysis.visible_elements || [],
          not_visible: photoAnalysis.not_visible || photoAnalysis.what_to_avoid_in_content || [],
          shot_type: photoAnalysis.shot_type || (contextMode === 'space' ? 'interior' : 'product'),
          is_exterior: photoAnalysis.is_exterior || false,
          space_type: photoAnalysis.space_type || '',
          architectural_elements: Array.isArray(photoAnalysis.architectural_elements) ? photoAnalysis.architectural_elements : [],
          atmosphere: photoAnalysis.atmosphere || '',
          // Include suggested angles for angle enforcement
          suggested_angles: photoAnalysis.suggested_angles || [],
          lighting_notes: photoAnalysis.lighting_notes || ''
        },
        scene_blueprint: photoAnalysis.scene_blueprint,
        // NEW: Include Visual DNA for angle enforcement + demographic preservation
        visual_dna: photoAnalysis.visual_dna || null
      };
      
      console.log('V2 Photo Context being sent:', JSON.stringify(v2PhotoContext, null, 2));
      if (v2PhotoContext.visual_dna) {
        console.log('✅ Visual DNA included with', v2PhotoContext.visual_dna.available_angles?.length || 0, 'angles');
      }
      
      const response = await api.generateFocusOptionsV2(v2PhotoContext, {
        num_options: 3,
        avoid_options: previousOptions.map(o => o.name),
        client_id: client?.id,
        client_name: client?.name,
        client_industry: client?.industry,
        brand_voice: client?.brand_voice,
        target_audience: client?.target_audience,
        campaign_id: campaignId,
        campaign_name: campaign?.name,
        creative_angle: campaign?.creative_angle
      });
      
      console.log('V2 Focus RAW response:', response);
      console.log('V2 Focus response.success:', response.success);
      console.log('V2 Focus response.options:', response.options);
      console.log('V2 Focus response.options?.length:', response.options?.length);
      
      if (response.success && response.options?.length > 0) {
        console.log('✅ Setting focus options:', response.options);
        setFocusOptions(response.options);
        setFocusQualityGate(response.quality_gate || null); // Store quality gate
        setExpandedOption(0);
        setFocusGenerated(true);
        
        // Log validation status
        if (response.validation_passed) {
          console.log('✅ Focus options passed constraint validation');
        } else {
          console.warn('⚠️ Focus options generated but validation not fully passed:', response.validation_notes);
        }
      } else {
        console.error('❌ V2 focus generation condition failed:');
        console.error('  response.success =', response.success);
        console.error('  response.options =', response.options);
        console.error('  response.options?.length =', response.options?.length);
        console.error('  Full response:', JSON.stringify(response, null, 2));
        setError('Failed to generate focus options. Please try again.');
      }
    } catch (err) {
      console.error('Focus options generation error:', err);
      
      if (err.isAuthError) {
        setError('Session expired. Redirecting to sign in...');
        return;
      }
      
      if (err.response?.status === 403) {
        const detail = err.response?.data?.detail || 'Not enough credits';
        setError(detail);
        return;
      }
      
      if (err.isAIError) {
        setError(`${err.message}${err.canRetry ? ' Click Regenerate to try again.' : ''}`);
      } else {
        const detail = err.response?.data?.detail;
        const errorMessage = err.response?.data?.user_message || 
          (typeof detail === 'string' ? detail : null) ||
          detail?.message ||
          (Array.isArray(detail) ? detail.map(e => e.msg || e.message).join(', ') : null) ||
          err.message || 'Failed to generate focus options. Please try again.';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle product image upload
  // Proceed from Step 1 (Photo+Context) to Step 2 (Focus)
  const handleProceedToFocus = async () => {
    // If photo uploaded but not analyzed yet, analyze first
    if (uploadedPhoto && contextMode && !photoAnalysis) {
      setAnalyzingPhoto(true);
      try {
        // Use V2 endpoint with literal constraint system
        const response = await api.analyzePhotoV2({
          image_base64: uploadedPhoto.base64,
          context_mode: contextMode,
          client_id: client?.id,
          campaign_id: campaignId
        });
        
        if (response.success && response.analysis) {
          const analysisWithMode = {
            ...response.analysis,
            _context_mode: contextMode,
            _analyzed_at: new Date().toISOString(),
            _version: 'v2',
            visible_elements: response.analysis.visible_elements || [],
            not_visible: response.analysis.not_visible || [],
            literal_description: response.analysis.literal_description,
            constraint: response.constraint,
            scene_blueprint: response.scene_blueprint,
            source_image_url: uploadedPhoto?.base64,
            has_virtual_rephotography: !!response.scene_blueprint
          };
          
          // Set state AND proceed - use the local variable for immediate access
          setPhotoAnalysis(analysisWithMode);
          setStep(2);
          
          // Pass analysis directly to avoid state timing issues
          await generateFocusOptionsWithAnalysis(analysisWithMode);
        } else {
          setError('Failed to analyze photo. Please try again.');
        }
      } catch (err) {
        console.error('Photo analysis error:', err);
        setError(err.message || 'Failed to analyze photo.');
      } finally {
        setAnalyzingPhoto(false);
      }
    } else if (!uploadedPhoto) {
      // No photo - just proceed
      console.log('DEBUG: No photo flow - setting step to 2');
      setStep(2);
      console.log('DEBUG: About to call generateFocusOptions (no photo)');
      generateFocusOptions();
    } else {
      setStep(2);
      // Generate focus options with existing photo analysis context
      generateFocusOptions();
    }
  };

  // New function that accepts analysis directly to avoid state timing issues
  const generateFocusOptionsWithAnalysis = async (analysis) => {
    if (!campaignId) {
      setError('Campaign ID is missing. Please try again.');
      return;
    }
    
    setLoading(true);
    setError('');
    setFocusGenerated(false);
    
    try {
      console.log('🔍 Using V2 Focus Generator with DIRECT analysis');
      console.log('Analysis primary_subject:', analysis?.primary_subject);
      console.log('Analysis is_exterior:', analysis?.is_exterior);
      console.log('Analysis visible_elements:', analysis?.visible_elements?.length);
      console.log('Analysis has Visual DNA:', !!analysis?.visual_dna);
      
      const v2PhotoContext = {
        context_mode: contextMode,
        analysis: {
          primary_subject: analysis.primary_subject || '',
          literal_description: analysis.literal_description || '',
          visible_elements: analysis.visible_elements || [],
          not_visible: analysis.not_visible || [],
          shot_type: analysis.shot_type || (contextMode === 'space' ? 'interior' : 'product'),
          is_exterior: analysis.is_exterior || false,
          space_type: analysis.space_type || '',
          architectural_elements: Array.isArray(analysis.architectural_elements) ? analysis.architectural_elements : [],
          atmosphere: analysis.atmosphere || '',
          // Include suggested angles for angle enforcement
          suggested_angles: analysis.suggested_angles || [],
          lighting_notes: analysis.lighting_notes || ''
        },
        scene_blueprint: analysis.scene_blueprint,
        // NEW: Include Visual DNA for angle enforcement + demographic preservation
        visual_dna: analysis.visual_dna || null
      };
      
      console.log('V2 Photo Context being sent:', JSON.stringify(v2PhotoContext, null, 2));
      if (v2PhotoContext.visual_dna) {
        console.log('✅ Visual DNA included with', v2PhotoContext.visual_dna.available_angles?.length || 0, 'angles');
      }
      if (highlightText.trim()) {
        console.log('✅ Highlight text provided:', highlightText.trim());
      }
      console.log('✅ Content style:', contentStyle);
      
      const response = await api.generateFocusOptionsV2(v2PhotoContext, {
        num_options: 3,
        avoid_options: [],
        client_id: client?.id,
        client_name: client?.name,
        client_industry: client?.industry,
        brand_voice: client?.brand_voice,
        target_audience: client?.target_audience,
        campaign_id: campaignId,
        campaign_name: campaign?.name,
        creative_angle: campaign?.creative_angle,
        highlight_text: highlightText.trim() || null,  // What user wants to highlight
        content_style: contentStyle  // clean, spotlight, or announcement - affects visual composition
      });
      
      console.log('V2 Focus response:', response);
      
      if (response.success && response.options?.length > 0) {
        console.log('✅ Setting focus options:', response.options);
        setFocusOptions(response.options);
        setFocusQualityGate(response.quality_gate || null); // Store quality gate
        setExpandedOption(0);
        setFocusGenerated(true);
      } else {
        console.error('❌ V2 focus generation failed:', response);
        setError('Failed to generate focus options. Please try again.');
      }
    } catch (err) {
      console.error('Focus options generation error:', err);
      setError(err.message || 'Failed to generate focus options. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOption = (option) => {
    setSelectedOption(option);
    setUseCustom(false);
    setStep(3); // Go directly to Generate step
  };

  // AI-powered overlay text suggestion
  const handleSuggestOverlayText = async () => {
    setSuggestingOverlay(true);
    try {
      // Build context for AI
      const context = {
        content_style: contentStyle,
        highlight_text: highlightText.trim() || null,
        photo_analysis: photoAnalysis ? {
          primary_subject: photoAnalysis.primary_subject,
          space_type: photoAnalysis.space_type,
          context_mode: contextMode
        } : null,
        client_name: client?.name || null,
        client_industry: client?.industry || null
      };
      
      const response = await api.suggestOverlayText(context);
      
      if (response.success) {
        setOverlayPrimary(response.primary || '');
        setOverlaySecondary(response.secondary || '');
        setOverlayTertiary(response.tertiary || '');
      }
    } catch (err) {
      console.error('Failed to suggest overlay text:', err);
      // Fallback to simple defaults if API fails
      if (contentStyle === 'spotlight') {
        setOverlayPrimary(photoAnalysis?.primary_subject?.toUpperCase() || 'PRODUCT NAME');
        setOverlaySecondary('A unique experience crafted with care');
        setOverlayTertiary('Available now');
      } else {
        setOverlayPrimary('NEW');
        setOverlaySecondary(highlightText || 'Something exciting is here');
        setOverlayTertiary('Learn more');
      }
    } finally {
      setSuggestingOverlay(false);
    }
  };

  const handleGenerateCustomDetails = async () => {
    if (!customBrief.trim()) return;
    setGeneratingCustom(true);
    setError('');
    
    // Build photo context if available
    const photoContextForCustom = photoAnalysis ? {
      context_mode: contextMode,
      ...photoAnalysis
    } : null;
    
    try {
      const response = await api.generateCustomFocusDetails(campaignId, customBrief, photoContextForCustom);
      const details = response.details;
      setSelectedOption(details);
      setStep(3); // Go directly to Generate step
    } catch (err) {
      if (err.isAIError) {
        setError(`${err.message}${err.canRetry ? ' Click "Generate Details" to try again.' : ''}`);
      } else {
        const detail = err.response?.data?.detail;
        const errorMessage = err.response?.data?.user_message || 
          (typeof detail === 'string' ? detail : null) ||
          detail?.message ||
          (Array.isArray(detail) ? detail.map(e => e.msg || e.message).join(', ') : null) ||
          'Failed to generate details. Please try again.';
        setError(errorMessage);
      }
    } finally {
      setGeneratingCustom(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 10 * 1024 * 1024;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    files.forEach(file => {
      if (!allowedTypes.includes(file.type)) {
        setError(`Invalid file type: ${file.name}`);
        return;
      }
      if (file.size > maxSize) {
        setError(`File too large: ${file.name}`);
        return;
      }
      setError('');
      const reader = new FileReader();
      reader.onloadend = () => setReferenceImages(prev => [...prev, reader.result]);
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setReferenceImages(prev => prev.filter((_, i) => i !== index));
  };

  // Platform helpers
  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        enabled: !prev[platform].enabled,
        types: !prev[platform].enabled ? [platformOptions[platform][0]] : []
      }
    }));
  };

  const toggleContentType = (platform, type) => {
    setSelectedPlatforms(prev => {
      const currentTypes = prev[platform].types;
      const newTypes = currentTypes.includes(type)
        ? currentTypes.filter(t => t !== type)
        : [...currentTypes, type];
      return {
        ...prev,
        [platform]: { ...prev[platform], types: newTypes, enabled: newTypes.length > 0 }
      };
    });
  };

  // Get number of detected angles from photo analysis
  const getDetectedAngles = () => {
    return photoAnalysis?.visual_dna?.available_angles?.length || 
           photoAnalysis?.photography_directions?.length || 
           3; // Default fallback
  };

  // Calculate totals - 1 idea per content type (simplified)
  const getTotalIdeas = () => {
    let total = 0;
    Object.values(selectedPlatforms || {}).forEach(p => {
      if (p?.enabled && p?.types) total += p.types.length;
    });
    return total; // 1 idea per format
  };

  const getTotalImages = () => {
    const ideas = getTotalIdeas();
    const imgCount = imagesPerIdea || getDetectedAngles(); // Default to detected angles
    return generateIndividualImages ? ideas * imgCount : ideas;
  };

  // Calculate total credits using the new system
  const getTotalCredits = () => {
    const ideas = getTotalIdeas();
    const images = getTotalImages();
    const costs = userCredits.creditCosts || CREDIT_COSTS;
    
    // Text generation: 4 credits per idea
    const textCredits = ideas * costs.generate_text;
    // Image generation: 2 credits per image
    const imageCredits = images * costs.generate_image;
    
    return textCredits + imageCredits;
  };

  const getCreditsRemaining = () => {
    return userCredits.remaining;
  };

  const hasInsufficientCredits = () => {
    return getTotalCredits() > getCreditsRemaining();
  };

  const getContentTypeIcon = (type) => {
    if (type.toLowerCase().includes('reel') || type.toLowerCase().includes('video')) return <FiVideo className="text-purple-500" />;
    if (type.toLowerCase().includes('carousel') || type.toLowerCase().includes('post')) return <FiImage className="text-blue-500" />;
    return <FiFileText className="text-green-500" />;
  };

  // Final submission - create focus AND generate content
  const handleGenerateContent = async () => {
    const totalIdeas = getTotalIdeas();
    if (totalIdeas === 0) {
      setError('Please select at least one platform and content type to generate.');
      return;
    }
    
    if (!selectedOption?.name || !selectedOption?.description) {
      setError('Please select a creative direction first.');
      return;
    }
    
    setLoading(true);
    setError('');
    setIsAIBusy(false);
    
    let createdFocus = null; // Declare outside try so it's accessible in catch
    
    try {
      // Build photography directions from selected option OR from photo analysis
      const photographyDirections = selectedOption?.photography_directions || photoAnalysis?.photography_directions || [];
      
      // Build focus description from selected option
      const focusDescription = `${selectedOption.description}${selectedOption.visual_style ? '\n\nVisual Style: ' + selectedOption.visual_style : ''}${selectedOption.tone ? '\n\nTone: ' + selectedOption.tone : ''}`;
      
      // Create the focus WITH photo context, scene blueprint, and photography directions
      const focusData = {
        campaign_id: campaignId,
        custom_name: selectedOption.name,
        custom_description: focusDescription,
        reference_images: referenceImages?.length > 0 ? referenceImages : null,
        // Pass context mode at top level for routing
        context_mode: contextMode,
        // Pass photo context for photography mode
        photo_context: photoAnalysis ? {
          context_mode: contextMode,
          analysis: photoAnalysis,
          has_virtual_rephotography: photoAnalysis.has_virtual_rephotography || false
        } : null,
        // Pass photography directions with full photography/visual/tone details
        photography_directions: photographyDirections?.length > 0 ? photographyDirections : null,
        // Pass scene blueprint for Virtual Re-Photography (space mode)
        scene_blueprint: photoAnalysis?.scene_blueprint || null,
        // Pass source image URL for image generation reference
        source_image_url: photoAnalysis?.source_image_url || uploadedPhoto?.base64 || null
      };

      console.log('Creating focus with:', {
        hasPhotoContext: !!focusData.photo_context,
        hasSceneBlueprint: !!focusData.scene_blueprint,
        hasSourceImage: !!focusData.source_image_url,
        photographyDirectionsCount: photographyDirections?.length || 0,
        hasVirtualRephotography: photoAnalysis?.has_virtual_rephotography
      });

      createdFocus = await api.createContentFocus(focusData, null);
      console.log('Focus created successfully:', createdFocus?.id);
      
      // Step 2: Generate content with the new focus
      const platforms = {};
      Object.keys(selectedPlatforms || {}).forEach(platform => {
        if (selectedPlatforms[platform]?.enabled && selectedPlatforms[platform]?.types?.length > 0) {
          platforms[platform] = selectedPlatforms[platform].types;
        }
      });

      if (Object.keys(platforms).length === 0) {
        setError('Please select at least one platform and content type');
        setLoading(false);
        return;
      }

      const contentRequest = {
        focus_id: createdFocus.id,
        platforms,
        quantity: 1, // Always 1 idea per format (simplified flow)
        additional_brief: additionalBrief || null,
        product_story: highlightText.trim() || null, // What user wants to highlight (influences captions)
        image_options: {
          ratio: imageRatio,
          quality: imageQuality,
          individual_images: generateIndividualImages,
          images_per_idea: imagesPerIdea || getDetectedAngles(), // Default to detected angles
          content_style: contentStyle, // Text overlay style: "clean", "spotlight", "announcement"
          render_text_on_image: renderTextOnImage, // If true, Nano Banana renders text directly on image
          // User-provided overlay text (optional - AI fills in blanks)
          overlay_primary: overlayPrimary.trim() || null,
          overlay_secondary: overlaySecondary.trim() || null,
          overlay_tertiary: overlayTertiary.trim() || null
        },
        // Pass photo context for image generation
        photo_context: photoAnalysis ? {
          context_mode: contextMode,
          analysis: photoAnalysis,
          original_image: uploadedPhoto?.base64 || null,
          visual_direction: photoAnalysis.visual_direction || null,
          photography_directions: photoAnalysis.photography_directions || photographyDirections || null
        } : null
      };

      console.log('Sending content generation request:', { focus_id: contentRequest.focus_id, platforms: Object.keys(platforms) });
      const result = await api.generateContent(contentRequest);
      console.log('Content generation result:', result);
      
      // Safety check for result
      if (!result) {
        throw new Error('No response from content generation');
      }
      
      if (result.generated_count < getTotalIdeas()) {
        console.log(`Generated ${result.generated_count} of ${getTotalIdeas()} requested ideas`);
      }
      
      // Save the photo to recent photos gallery (if new upload, not from recent)
      if (uploadedPhoto && photoAnalysis && !uploadedPhoto.isFromRecent && client?.id) {
        try {
          await api.saveClientPhoto(client.id, {
            url: uploadedPhoto.base64, // For now, store base64. Could upload to CDN later.
            name: uploadedPhoto.name,
            context_mode: contextMode,
            analysis_summary: photoAnalysis.primary_subject || photoAnalysis.space_type || 'photo'
          });
          console.log('Photo saved to recent photos gallery');
        } catch (photoSaveErr) {
          // Non-critical, just log
          console.warn('Could not save photo to gallery:', photoSaveErr);
        }
      }
      
      // Generation complete - go to Content Library
      setRetryCount(0);
      setIsAIBusy(false);
      onSuccess();
    } catch (err) {
      console.error('Content generation error:', err);
      console.error('Error details:', { 
        message: err.message, 
        code: err.code, 
        response: err.response?.status,
        data: err.response?.data 
      });
      const errorData = err.response?.data;
      
      // Check if this is a timeout/network/proxy error where content might have been created
      // 502 Bad Gateway often means the proxy timed out but backend is still processing
      const isTimeoutOrProxyError = err.code === 'ECONNABORTED' || 
        err.response?.status === 502 ||  // Bad Gateway - proxy timeout
        err.response?.status === 504 ||  // Gateway Timeout
        err.message?.toLowerCase().includes('timeout') ||
        err.message?.toLowerCase().includes('network error') ||
        !err.response; // No response usually means network/timeout issue
      
      // If timeout/proxy error, poll for content creation (backend might still be processing)
      if (isTimeoutOrProxyError && createdFocus?.id) {
        console.log('Detected timeout/proxy error, polling for content creation...');
        
        // Poll up to 6 times with increasing delays: 5s, 10s, 15s, 20s, 25s, 30s (total ~2 mins)
        const pollDelays = [5000, 10000, 15000, 20000, 25000, 30000];
        
        for (let i = 0; i < pollDelays.length; i++) {
          try {
            console.log(`Poll attempt ${i + 1}/${pollDelays.length}, waiting ${pollDelays[i]/1000}s...`);
            await new Promise(resolve => setTimeout(resolve, pollDelays[i]));
            
            // Check if content was created for this focus
            const contentCheck = await api.listContentIdeas({ focus_id: createdFocus.id }, 1, 1);
            console.log(`Poll ${i + 1} result:`, contentCheck?.total || 0, 'items');
            
            if (contentCheck?.items?.length > 0 || contentCheck?.total > 0) {
              // Content was created! Backend completed successfully
              console.log('Content was created despite proxy timeout - treating as success');
              setRetryCount(0);
              setIsAIBusy(false);
              onSuccess();
              return;
            }
          } catch (checkErr) {
            console.warn(`Poll ${i + 1} failed:`, checkErr.message);
          }
        }
        
        // After all polls, still no content - show appropriate message
        console.log('Content not found after polling - generation may have failed');
        setError('Generation is taking longer than expected. Please check your Content Library or try again.');
        setLoading(false);
        return;
      }
      
      // Check if AI is busy (provider issues, timeouts)
      const isAIBusyError = errorData?.is_ai_busy || 
        errorData?.type === 'ai_provider_error' ||
        err.message?.toLowerCase().includes('timeout') ||
        err.message?.toLowerCase().includes('502') ||
        err.message?.toLowerCase().includes('high demand');
      
      if (isAIBusyError) {
        setIsAIBusy(true);
        setRetryCount(prev => prev + 1);
        setError(errorData?.user_message || 'Our AI is experiencing high demand right now. Please try again in a few moments.');
        return;
      }
      
      if (err.isAIError) {
        setIsAIBusy(err.message?.toLowerCase().includes('busy') || err.message?.toLowerCase().includes('demand'));
        setError(`${err.message}${err.canRetry ? ' Click "Generate" to try again.' : ''}`);
        return;
      }
      
      if (err.response?.status === 403 && errorData?.generated_so_far > 0) {
        setError(`Credit limit reached. Generated ${errorData.generated_so_far} ideas before limit. Your content has been saved - view it in the Content Library.`);
        setTimeout(() => onSuccess(), 3000);
        return;
      }
      
      // User-friendly error messages for common issues
      let errorMessage = errorData?.user_message || errorData?.error || errorData?.detail?.message || 
        (typeof errorData?.detail === 'string' ? errorData.detail : null) ||
        (Array.isArray(errorData?.detail) ? errorData.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : null) ||
        err.message || 'Failed to generate content. Please try again.';
      
      // Make error messages more user-friendly
      if (errorMessage.toLowerCase().includes('timeout') || errorMessage.toLowerCase().includes('timed out')) {
        errorMessage = 'The request took too long. Our AI might be busy - please try again in a moment.';
        setIsAIBusy(true);
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div 
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card rounded-xl w-full max-w-4xl shadow-2xl border border-border/50 flex flex-col"
        style={{ maxHeight: '85vh' }}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-5 border-b border-border/50">
          <div>
            <h2 className="text-xl font-bold text-foreground">Create Content</h2>
            <p className="text-sm text-muted-foreground mt-1">for {campaign.name}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="close-modal">
            <FiX size={24} />
          </button>
        </div>

        {/* Progress - 3 STEPS */}
        <div className="flex-shrink-0 px-6 pt-4">
          <div className="flex items-center space-x-2 text-sm">
            <span className={`px-3 py-1 rounded-full ${step >= 1 ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground'}`}>1. Photo</span>
            <span className="text-muted-foreground">→</span>
            <span className={`px-3 py-1 rounded-full ${step >= 2 ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground'}`}>2. Direction</span>
            <span className="text-muted-foreground">→</span>
            <span className={`px-3 py-1 rounded-full ${step >= 3 ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground'}`}>3. Generate</span>
          </div>
        </div>

        {/* Content */}
        <div ref={contentRef} className="flex-1 overflow-y-auto p-6">
          {/* Generation Progress - shows during content generation */}
          {loading && step === 3 && (
            <div className="mb-4">
              <GenerationProgress 
                isGenerating={loading}
                retryCount={retryCount}
                error={null}
              />
            </div>
          )}
          
          {/* Error Display with AI Busy Indicator */}
          {error && !loading && (
            <GenerationProgress 
              isGenerating={false}
              retryCount={retryCount}
              error={error}
              onRetry={isAIBusy || error.toLowerCase().includes('try again') ? handleGenerateContent : null}
            />
          )}
          
          {/* Simple error for non-generation errors */}
          {error && !loading && step !== 3 && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* STEP 1: Photo + Context (NEW GUIDED CONTEXT SYSTEM) */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 mb-4">
                  <FiImage className="text-purple-400" size={32} />
                </div>
                <h3 className="text-xl font-bold text-foreground">Set Your Content Direction</h3>
                <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                  Choose how you want AI to generate your content
                </p>
              </div>

              {/* Photo Upload Area */}
              <div className="flex justify-center">
                <input
                  type="file"
                  ref={photoFileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  className="hidden"
                />
                
                {!uploadedPhoto ? (
                  <div className="w-full max-w-2xl space-y-4">
                    {/* Upload button */}
                    <button
                      onClick={() => photoFileInputRef.current?.click()}
                      className="w-full h-40 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-3 hover:border-purple-500/50 hover:bg-purple-500/5 transition-all group"
                      data-testid="upload-photo-btn"
                    >
                      <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                        <FiUpload className="text-purple-400" size={20} />
                      </div>
                      <div className="text-center">
                        <p className="font-medium text-foreground">Drop an image or click to upload</p>
                        <p className="text-sm text-muted-foreground mt-1">JPG, PNG up to 10MB</p>
                      </div>
                    </button>
                    
                    {/* Recent Photos Gallery */}
                    {recentPhotos?.length > 0 && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-muted-foreground">Recent Photos</h4>
                          <span className="text-xs text-muted-foreground">{recentPhotos.length} saved</span>
                        </div>
                        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                          {recentPhotos.slice(0, 12).map((photo) => (
                            <button
                              key={photo.id}
                              onClick={() => selectRecentPhoto(photo)}
                              className="relative aspect-square rounded-lg overflow-hidden border-2 border-border hover:border-purple-500 transition-all group"
                              title={photo.name || 'Recent photo'}
                            >
                              <img 
                                src={photo.thumbnail_url || photo.url} 
                                alt={photo.name || 'Recent'} 
                                className="w-full h-full object-cover"
                              />
                              {photo.context_mode && (
                                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] py-0.5 text-center">
                                  {photo.context_mode === 'product' && '📦'}
                                  {photo.context_mode === 'space' && '🏠'}
                                </div>
                              )}
                              <div className="absolute inset-0 bg-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <FiCheck className="text-white" size={20} />
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Loading state for recent photos */}
                    {loadingRecentPhotos && (!recentPhotos || recentPhotos.length === 0) && (
                      <div className="flex items-center justify-center py-4 text-muted-foreground text-sm">
                        <FiLoader className="animate-spin mr-2" size={16} />
                        Loading recent photos...
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-full max-w-md">
                    <div className="relative rounded-xl overflow-hidden border-2 border-purple-500 bg-card">
                      <img 
                        src={uploadedPhoto.preview} 
                        alt="Uploaded" 
                        className="w-full h-48 object-cover"
                      />
                      <button
                        onClick={removePhoto}
                        className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                        title="Remove photo"
                      >
                        <FiX size={16} />
                      </button>
                      <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 rounded text-white text-xs">
                        {uploadedPhoto.name}
                        {uploadedPhoto.isFromRecent && <span className="ml-1 text-purple-300">(saved)</span>}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Context Mode Selection - Show when photo is uploaded */}
              {uploadedPhoto && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-foreground mb-4 text-center">What&apos;s in this photo?</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                    {CONTEXT_MODES.filter(m => m.id === 'product' || m.id === 'space').map((mode) => (
                      <button
                        key={mode.id}
                        onClick={() => {
                          // Clear analysis if changing mode (needs re-analysis)
                          if (contextMode !== mode.id && photoAnalysis) {
                            setPhotoAnalysis(null);
                          }
                          setContextMode(mode.id);
                        }}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          contextMode === mode.id
                            ? 'border-purple-500 bg-purple-500/10 ring-2 ring-purple-500/30'
                            : 'border-border hover:border-purple-500/50 hover:bg-purple-500/5'
                        }`}
                        data-testid={`context-mode-${mode.id}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">{mode.icon}</span>
                          <div>
                            <p className="font-semibold text-foreground">{mode.label}</p>
                            <p className="text-xs text-purple-400">{mode.tagline}</p>
                          </div>
                        </div>
                        
                        {/* Focus section */}
                        <div className="mt-3">
                          <p className="text-xs font-medium text-muted-foreground mb-1">AI WILL FOCUS ON:</p>
                          <div className="flex flex-wrap gap-1">
                            {mode.focus.slice(0, 3).map((item, i) => (
                              <span key={i} className="px-2 py-0.5 bg-emerald-500/10 text-emerald-500 text-xs rounded">
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        {/* Covers section */}
                        <div className="mt-2">
                          <p className="text-xs font-medium text-muted-foreground mb-1">COVERS:</p>
                          <p className="text-xs text-muted-foreground line-clamp-1">
                            {mode.covers.slice(0, 4).join(', ')}
                          </p>
                        </div>
                        
                        {/* What AI ignores */}
                        {contextMode === mode.id && (
                          <div className="mt-2 pt-2 border-t border-border">
                            <p className="text-xs text-amber-500/80">
                              <span className="font-medium">Ignores:</span> {mode.aiIgnores}
                            </p>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Analyze Photo Button */}
              {uploadedPhoto && contextMode && !photoAnalysis && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={analyzePhoto}
                    disabled={analyzingPhoto}
                    className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50 font-medium"
                    data-testid="analyze-photo-btn"
                  >
                    {analyzingPhoto ? (
                      <>
                        <FiLoader className="animate-spin" size={18} />
                        <span>Analyzing {contextMode}...</span>
                      </>
                    ) : (
                      <>
                        <FiZap size={18} />
                        <span>Analyze Photo</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Photo Analysis Result */}
              {photoAnalysis && (
                <div className="mt-6 p-4 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl">
                  {/* Warning if analysis mode doesn't match current selection */}
                  {photoAnalysis._context_mode && photoAnalysis._context_mode !== contextMode && (
                    <div className="mb-3 p-2 bg-amber-500/20 border border-amber-500/50 rounded text-amber-400 text-xs">
                      ⚠️ This analysis was done in {photoAnalysis._context_mode} mode. Click "Analyze Photo" again to re-analyze as {contextMode}.
                    </div>
                  )}
                  <div className="flex items-start gap-4">
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border-2 border-purple-500">
                      <img src={uploadedPhoto.preview} alt="Analyzed" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">{CONTEXT_MODES.find(m => m.id === contextMode)?.icon}</span>
                        <p className="font-semibold text-foreground">
                          {contextMode === 'product' && (photoAnalysis.product_name || 'Product identified')}
                          {contextMode === 'space' && (photoAnalysis.space_type || 'Space identified')}
                        </p>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {contextMode === 'product' && (photoAnalysis.distinctive_features?.slice(0, 2).join(', ') || 'Distinctive product features identified')}
                        {contextMode === 'space' && (photoAnalysis.atmosphere || 'Atmosphere and layout analyzed')}
                      </p>
                      {photoAnalysis.content_hooks && photoAnalysis.content_hooks.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-purple-400 font-medium">Sample hooks:</p>
                          <p className="text-xs text-muted-foreground italic">&ldquo;{photoAnalysis.content_hooks[0]}&rdquo;</p>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={removePhoto}
                      className="p-2 text-purple-300 hover:text-white hover:bg-purple-500/30 rounded-lg transition-colors flex-shrink-0"
                      title="Remove and start over"
                    >
                      <FiX size={20} />
                    </button>
                  </div>
                </div>
              )}

              {/* What should we highlight? - Shows for both photo and no-photo flows */}
              {(photoAnalysis || (!uploadedPhoto && !analyzingPhoto)) && (
                <div className="mt-4">
                  <div className="p-4 border border-border bg-secondary/30 rounded-xl">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {photoAnalysis 
                        ? <>What should we highlight? <span className="text-muted-foreground font-normal">(optional)</span></>
                        : <>What's this about?</>
                      }
                    </label>
                    <textarea
                      value={highlightText}
                      onChange={(e) => setHighlightText(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-foreground placeholder:text-muted-foreground text-sm resize-none"
                      placeholder={photoAnalysis 
                        ? "e.g., Handmade in Jaipur, uses organic cotton, limited edition..."
                        : "e.g., New summer collection launch, artisan coffee blend, store anniversary..."
                      }
                      data-testid="highlight-input"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {photoAnalysis 
                        ? "This helps AI craft better angles and visuals for your content"
                        : "Tell us what you want to create content about"
                      }
                    </p>
                  </div>
                </div>
              )}

              {/* Content Style Selection - Shows for both photo and no-photo flows */}
              {(photoAnalysis || (!uploadedPhoto && !analyzingPhoto)) && (
                <div className="mt-4">
                  <div className="p-4 border border-border bg-secondary/30 rounded-xl">
                    <label className="block text-sm font-medium text-foreground mb-3">
                      Content style
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {/* Clean - Image only */}
                      <button
                        onClick={() => setContentStyle('clean')}
                        className={`p-3 rounded-xl border-2 transition-all ${
                          contentStyle === 'clean'
                            ? 'border-primary bg-primary/10 ring-2 ring-primary/30'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid="content-style-clean"
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center mb-2 border border-border">
                            <FiImage className="text-muted-foreground" size={18} />
                          </div>
                          <span className="font-medium text-foreground text-sm">Clean</span>
                          <span className="text-xs text-muted-foreground">Image only</span>
                        </div>
                      </button>
                      
                      {/* Spotlight - Name + desc */}
                      <button
                        onClick={() => setContentStyle('spotlight')}
                        className={`p-3 rounded-xl border-2 transition-all ${
                          contentStyle === 'spotlight'
                            ? 'border-primary bg-primary/10 ring-2 ring-primary/30'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid="content-style-spotlight"
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center mb-2 border border-border relative">
                            <FiImage className="text-muted-foreground" size={18} />
                            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-1 py-0.5 bg-purple-500 rounded text-[7px] text-white font-bold">
                              TEXT
                            </div>
                          </div>
                          <span className="font-medium text-foreground text-sm">Spotlight</span>
                          <span className="text-xs text-muted-foreground">Name + desc</span>
                        </div>
                      </button>
                      
                      {/* Announcement - Bold headline */}
                      <button
                        onClick={() => setContentStyle('announcement')}
                        className={`p-3 rounded-xl border-2 transition-all ${
                          contentStyle === 'announcement'
                            ? 'border-primary bg-primary/10 ring-2 ring-primary/30'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid="content-style-announcement"
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center mb-2 border border-border relative">
                            <FiImage className="text-muted-foreground" size={18} />
                            <div className="absolute -top-1 left-1/2 -translate-x-1/2 px-1 py-0.5 bg-amber-500 rounded text-[7px] text-white font-bold">
                              NEW
                            </div>
                          </div>
                          <span className="font-medium text-foreground text-sm">Announce</span>
                          <span className="text-xs text-muted-foreground">Bold headline</span>
                        </div>
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">
                      {contentStyle === 'clean' && 'Full-frame visuals, no text overlay'}
                      {contentStyle === 'spotlight' && 'AI will compose with space for product name & description'}
                      {contentStyle === 'announcement' && 'AI will compose with space for bold headline text'}
                    </p>
                    
                    {/* Text Overlay Input Fields - show when spotlight or announcement selected */}
                    {contentStyle !== 'clean' && (
                      <div className="mt-4 pt-4 border-t border-border space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-muted-foreground">
                            What text should appear on the image?
                          </p>
                          <button
                            onClick={handleSuggestOverlayText}
                            disabled={suggestingOverlay}
                            className="flex items-center gap-1.5 text-xs text-purple-400 hover:text-purple-300 transition-colors disabled:opacity-50"
                            data-testid="suggest-overlay-btn"
                          >
                            {suggestingOverlay ? (
                              <>
                                <FiLoader className="animate-spin" size={12} />
                                <span>Thinking...</span>
                              </>
                            ) : (
                              <>
                                <FiZap size={12} />
                                <span>Let FirstCut suggest</span>
                              </>
                            )}
                          </button>
                        </div>
                        
                        {/* Primary - Product Name / Headline */}
                        <div>
                          <label className="block text-xs font-medium text-purple-300 mb-1.5">
                            {contentStyle === 'spotlight' ? 'Product Name' : 'Headline'}
                            <span className="text-muted-foreground font-normal ml-1">(optional)</span>
                          </label>
                          <input
                            type="text"
                            value={overlayPrimary}
                            onChange={(e) => setOverlayPrimary(e.target.value)}
                            placeholder={contentStyle === 'spotlight' ? 'e.g., DIRTY COFFEE' : 'e.g., NOW OPEN'}
                            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-foreground placeholder:text-muted-foreground text-sm"
                            data-testid="overlay-primary-input"
                          />
                        </div>
                        
                        {/* Secondary - Description / Main Text */}
                        <div>
                          <label className="block text-xs font-medium text-purple-300 mb-1.5">
                            {contentStyle === 'spotlight' ? 'Description' : 'Main Text'}
                            <span className="text-muted-foreground font-normal ml-1">(optional)</span>
                          </label>
                          <textarea
                            value={overlaySecondary}
                            onChange={(e) => setOverlaySecondary(e.target.value)}
                            placeholder={contentStyle === 'spotlight' 
                              ? 'e.g., A thrilling sensation as hot and cold collide' 
                              : 'e.g., Summer Collection 2024'}
                            rows={2}
                            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-foreground placeholder:text-muted-foreground text-sm resize-none"
                            data-testid="overlay-secondary-input"
                          />
                        </div>
                        
                        {/* Tertiary - Tagline / Supporting Detail */}
                        <div>
                          <label className="block text-xs font-medium text-purple-300 mb-1.5">
                            {contentStyle === 'spotlight' ? 'Tagline' : 'Supporting Detail'}
                            <span className="text-muted-foreground font-normal ml-1">(optional)</span>
                          </label>
                          <input
                            type="text"
                            value={overlayTertiary}
                            onChange={(e) => setOverlayTertiary(e.target.value)}
                            placeholder={contentStyle === 'spotlight' 
                              ? 'e.g., Limited release, all May' 
                              : 'e.g., Available June 1st'}
                            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-foreground placeholder:text-muted-foreground text-sm"
                            data-testid="overlay-tertiary-input"
                          />
                        </div>
                        
                        {/* Render Text on Image Toggle */}
                        <div className="mt-4 pt-4 border-t border-border">
                          <label className="flex items-center justify-between cursor-pointer group">
                            <div className="flex-1">
                              <span className="text-sm font-medium text-foreground">Render text on image</span>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                AI will beautifully integrate your text into the design
                              </p>
                            </div>
                            <div className="relative ml-4">
                              <input
                                type="checkbox"
                                checked={renderTextOnImage}
                                onChange={(e) => setRenderTextOnImage(e.target.checked)}
                                className="sr-only peer"
                                data-testid="render-text-on-image-toggle"
                              />
                              <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:bg-purple-500 transition-colors"></div>
                              <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                            </div>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Brand Type Mismatch Notification */}
              {brandTypeMismatch.show && (
                <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="text-blue-400 mt-0.5">
                      <FiInfo size={18} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-blue-300 font-medium">Content Type Insight</p>
                      <p className="text-sm text-muted-foreground mt-1">{brandTypeMismatch.message}</p>
                      {brandTypeMismatch.detected && (
                        <p className="text-xs text-blue-400/70 mt-2">
                          Detected type: <span className="font-medium">{brandTypeMismatch.detected}</span> 
                          {brandTypeMismatch.confidence && ` (${Math.round(brandTypeMismatch.confidence * 100)}% confidence)`}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => setBrandTypeMismatch({ show: false, message: '', detected: null, confidence: 0 })}
                      className="text-blue-400 hover:text-blue-300 p-1"
                      title="Dismiss"
                    >
                      <FiX size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 2: Choose Direction */}
          {step === 2 && (
            <>
              {loading && (!focusOptions || focusOptions.length === 0) ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <FiLoader className="animate-spin text-primary mb-4" size={48} />
                  <p className="text-muted-foreground">
                    {photoAnalysis 
                      ? `Generating creative directions for your ${photoAnalysis.primary_subject || photoAnalysis.space_type || 'photo'}...`
                      : 'Generating creative directions...'
                    }
                  </p>
                  {photoAnalysis && (
                    <p className="text-sm text-purple-400 mt-2">FirstCut is crafting directions based on your {contextMode} photo</p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">Choose a Creative Direction</h3>
                      <p className="text-sm text-muted-foreground">
                        {photoAnalysis 
                          ? `Directions tailored for your ${contextMode} photo`
                          : 'This defines how your content will look and feel'
                        }
                      </p>
                    </div>
                    <button 
                      onClick={() => generateFocusOptions(true)} 
                      disabled={loading} 
                      className="flex items-center space-x-1 text-sm text-muted-foreground hover:text-foreground"
                      title="Uses 2 credits"
                    >
                      <FiLoader className={loading ? 'animate-spin' : ''} size={14} />
                      <span>Regenerate (2 credits)</span>
                    </button>
                  </div>

                  {/* Photo context reminder banner */}
                  {photoAnalysis && uploadedPhoto && (
                    <div className="flex items-center gap-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border border-purple-500">
                        <img src={uploadedPhoto.preview} alt="Context" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-purple-300">
                          {CONTEXT_MODES.find(m => m.id === contextMode)?.icon} {contextMode === 'product' ? photoAnalysis.product_name : contextMode === 'space' ? photoAnalysis.space_type : photoAnalysis.moment_description}
                        </p>
                        <p className="text-xs text-muted-foreground">Content will be guided by this {contextMode} context</p>
                      </div>
                    </div>
                  )}

                  {/* Quality Gate Indicator for Focus Options */}
                  {focusQualityGate && (focusQualityGate.items_retried > 0 || focusQualityGate.best_effort_count > 0) && (
                    <div className="mb-4">
                      <QualityGateIndicator qualityGate={focusQualityGate} itemCount={focusOptions?.length || 0} />
                    </div>
                  )}

                  {(focusOptions || []).map((option, idx) => (
                    <div key={idx} className={`border-2 rounded-xl overflow-hidden transition-all ${expandedOption === idx ? 'border-primary shadow-lg shadow-primary/10' : 'border-border'} ${
                      option.recommendation === 'skip' ? 'opacity-60' : ''
                    }`}>
                      <button
                        onClick={() => setExpandedOption(expandedOption === idx ? -1 : idx)}
                        className="w-full p-4 flex items-center justify-between text-left hover:bg-secondary"
                        data-testid={`focus-option-${idx}`}
                      >
                        <div className="flex-1 pr-4">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h4 className="text-lg font-bold text-foreground">{option.name}</h4>
                            {/* Quality Score Badge */}
                            <QualityScoreBadge 
                              score={option.marketing_score}
                              size="sm"
                            />
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{option.description}</p>
                          {/* Score verdict */}
                          {(option.verdict || option.score_reasoning?.verdict) && (
                            <p className="text-xs text-primary mt-1 italic">💡 {option.verdict || option.score_reasoning?.verdict}</p>
                          )}
                        </div>
                        {expandedOption === idx ? <FiChevronUp className="flex-shrink-0" /> : <FiChevronDown className="flex-shrink-0" />}
                      </button>

                      {expandedOption === idx && (
                        <div className="p-4 pt-0 border-t border-border bg-secondary/50">
                          {/* Score Details */}
                          {option.score_reasoning && (option.score_reasoning.strengths?.length > 0 || option.score_reasoning.weaknesses?.length > 0) && (
                            <div className="mb-4 p-3 bg-card rounded-lg border border-border">
                              <h5 className="text-sm font-semibold text-foreground mb-2">Marketing Analysis</h5>
                              {option.score_reasoning.strengths?.length > 0 && (
                                <div className="mb-2">
                                  <span className="text-xs text-green-400 font-medium">Strengths:</span>
                                  <ul className="text-xs text-muted-foreground ml-3">
                                    {option.score_reasoning.strengths.map((s, i) => (
                                      <li key={i}>• {s}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {option.score_reasoning.weaknesses?.length > 0 && (
                                <div>
                                  <span className="text-xs text-yellow-400 font-medium">Could improve:</span>
                                  <ul className="text-xs text-muted-foreground ml-3">
                                    {option.score_reasoning.weaknesses.map((w, i) => (
                                      <li key={i}>• {w}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                          
                          <div className="mb-4">
                            <h5 className="text-sm font-semibold text-foreground mb-2 flex items-center">
                              <FiImage className="mr-2 text-blue-500" /> Visual Style
                            </h5>
                            <p className="text-sm text-foreground bg-card p-3 rounded-lg border border-border">{option.visual_style || 'No visual style specified'}</p>
                          </div>
                          
                          {/* Photography Directions - Shot ideas for user's actual space */}
                          {option.photography_directions && option.photography_directions.length > 0 && (
                            <div className="mb-4">
                              <h5 className="text-sm font-semibold text-foreground mb-2 flex items-center">
                                <FiCamera className="mr-2 text-amber-500" /> 📸 Photography Directions
                              </h5>
                              <p className="text-xs text-muted-foreground mb-3">Shot ideas for YOUR actual space/product</p>
                              <div className="space-y-3">
                                {option.photography_directions.slice(0, 2).map((cd, cdIdx) => (
                                  <div key={cdIdx} className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 p-4 rounded-lg border border-amber-500/20">
                                    <div className="font-medium text-amber-600 dark:text-amber-400 mb-1">"{cd.title}"</div>
                                    {cd.concept && <p className="text-sm text-foreground mb-2">{cd.concept}</p>}
                                    {Array.isArray(cd.what_to_capture) && cd.what_to_capture.length > 0 && (
                                      <div className="mt-2">
                                        <span className="text-xs font-semibold text-muted-foreground uppercase">What to Capture:</span>
                                        <ul className="mt-1 text-xs text-foreground/80 space-y-1">
                                          {cd.what_to_capture.slice(0, 3).map((shot, shotIdx) => (
                                            <li key={shotIdx} className="flex items-start">
                                              <span className="mr-2">•</span>
                                              <span>{shot}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                      {cd.timing && <span className="px-2 py-1 bg-amber-500/20 rounded-full">⏰ {cd.timing}</span>}
                                      {cd.mood && <span className="px-2 py-1 bg-purple-500/20 rounded-full">✨ {cd.mood}</span>}
                                      {cd.technique && <span className="px-2 py-1 bg-blue-500/20 rounded-full">📷 {cd.technique}</span>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <div className="mb-4">
                            <h5 className="text-sm font-semibold text-foreground mb-2 flex items-center">
                              <FiFileText className="mr-2 text-green-500" /> Content Tone
                            </h5>
                            <p className="text-sm text-foreground bg-card p-3 rounded-lg border border-border">{option.tone || 'No tone specified'}</p>
                          </div>
                          {option.example_content && (
                            <div className="mb-4">
                              <h5 className="text-sm font-semibold text-foreground mb-2">Example Content</h5>
                              <div className="space-y-3">
                                {/* Handle both array and object formats */}
                                {Array.isArray(option.example_content) ? (
                                  option.example_content.map((ex, i) => (
                                    <div key={i} className="flex items-start space-x-3 bg-card p-3 rounded-lg border border-border">
                                      {getContentTypeIcon(ex.type)}
                                      <div>
                                        <span className="text-xs font-semibold text-muted-foreground uppercase">{ex.type}</span>
                                        <p className="text-sm text-foreground">{ex.concept}</p>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="space-y-2">
                                    {/* Post Example */}
                                    {(option.example_content.hook || option.example_content.caption) && (
                                      <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 p-4 rounded-lg border border-blue-500/20">
                                        <div className="flex items-center mb-2">
                                          <FiImage className="mr-2 text-blue-500" />
                                          <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase">Post</span>
                                        </div>
                                        {option.example_content.hook && (
                                          <p className="text-sm font-medium text-foreground mb-1">{option.example_content.hook}</p>
                                        )}
                                        {option.example_content.caption && (
                                          <p className="text-sm text-foreground/80">{option.example_content.caption}</p>
                                        )}
                                        {option.example_content.cta && (
                                          <p className="text-xs text-muted-foreground mt-2 italic">{option.example_content.cta}</p>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Story Example */}
                                    {option.example_content.story_idea && (
                                      <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 p-4 rounded-lg border border-purple-500/20">
                                        <div className="flex items-center mb-2">
                                          <FiCamera className="mr-2 text-purple-500" />
                                          <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase">Story</span>
                                        </div>
                                        <p className="text-sm text-foreground">{option.example_content.story_idea}</p>
                                      </div>
                                    )}
                                    
                                    {/* Reel Example */}
                                    {option.example_content.reel_concept && (
                                      <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 p-4 rounded-lg border border-orange-500/20">
                                        <div className="flex items-center mb-2">
                                          <FiVideo className="mr-2 text-orange-500" />
                                          <span className="text-xs font-semibold text-orange-600 dark:text-orange-400 uppercase">Reel</span>
                                        </div>
                                        <p className="text-sm text-foreground">{option.example_content.reel_concept}</p>
                                      </div>
                                    )}
                                    
                                    {/* Visual Direction if present */}
                                    {option.example_content.visual_direction && (
                                      <div className="bg-card p-3 rounded-lg border border-border">
                                        <p className="text-sm text-foreground"><span className="font-semibold">Visual:</span> {option.example_content.visual_direction}</p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          <button
                            onClick={() => handleSelectOption(option)}
                            className="w-full mt-2 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 font-medium flex items-center justify-center space-x-2"
                            data-testid={`select-focus-${idx}`}
                          >
                            <FiCheck />
                            <span>Select This Direction</span>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Custom Option */}
                  <div className="mt-6 pt-4 border-t border-border">
                    <div className="p-5 border-2 border-dashed border-purple-500/30 rounded-xl bg-purple-500/5">
                      <h4 className="font-semibold text-foreground">Create Custom Direction</h4>
                      <p className="text-sm text-muted-foreground mt-1 mb-4">Describe your vision and we&apos;ll generate the details</p>
                      <div className="space-y-4">
                        <textarea
                          value={customBrief}
                          onChange={(e) => setCustomBrief(e.target.value)}
                          rows={3}
                          className="w-full px-4 py-3 bg-secondary border border-border rounded-lg focus:ring-2 focus:ring-purple-500 text-foreground placeholder:text-muted-foreground"
                          placeholder={photoAnalysis 
                            ? `e.g., Show the ${photoAnalysis.product_name || photoAnalysis.space_type || 'subject'} in a luxurious modern setting...`
                            : 'e.g., Heritage-style shots with a modern twist...'
                          }
                          data-testid="custom-brief-input"
                        />
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Reference Images (Optional)</label>
                          <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" id="custom-img-upload" />
                          <label htmlFor="custom-img-upload" className="flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed border-border rounded-lg hover:border-purple-400 cursor-pointer bg-secondary">
                            <FiUpload className="text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Upload reference images</span>
                          </label>
                          {referenceImages?.length > 0 && (
                            <div className="mt-3 grid grid-cols-4 gap-2">
                              {referenceImages.map((img, idx) => (
                                <div key={idx} className="relative group">
                                  <img src={img} alt="" className="w-full h-16 object-cover rounded-lg border" />
                                  <button onClick={() => removeImage(idx)} className="absolute top-1 right-1 bg-red-500/100 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100">
                                    <FiX size={12} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-3">
                          <button
                            onClick={handleGenerateCustomDetails}
                            disabled={!customBrief.trim() || generatingCustom}
                            className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                            data-testid="generate-custom-btn"
                          >
                            {generatingCustom ? <FiLoader className="animate-spin" /> : null}
                            <span>{generatingCustom ? 'Generating...' : 'Generate Direction'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* STEP 3: Configure & Generate */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Configure Content Generation</h3>
                <button onClick={() => setStep(2)} className="text-sm text-primary hover:text-primary/80">← Back</button>
              </div>

              {/* Direction Summary */}
              <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                <div className="flex items-start gap-4">
                  {photoAnalysis && uploadedPhoto && (
                    <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 border-2 border-purple-500">
                      <img src={uploadedPhoto.preview} alt="Context" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-foreground">{selectedOption?.name}</h4>
                      <QualityScoreBadge 
                        score={selectedOption?.marketing_score}
                        size="sm"
                      />
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{selectedOption?.description}</p>
                    {photoAnalysis && (
                      <p className="text-xs text-purple-400 mt-2">Based on: {photoAnalysis.primary_subject || photoAnalysis.space_type || 'your photo'}</p>
                    )}
                    {highlightText && (
                      <p className="text-xs text-emerald-400 mt-1">Highlighting: {(highlightText.length || 0) > 50 ? highlightText.substring(0, 50) + '...' : highlightText}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Platforms */}
                <div className="lg:col-span-2 space-y-4">
                  <h4 className="font-semibold text-foreground">Platforms & Content Types</h4>
                  {Object.keys(platformOptions).map(platform => (
                    <div key={platform} className="border border-border rounded-lg p-4 bg-secondary/30">
                      <button
                        onClick={() => togglePlatform(platform)}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                          selectedPlatforms[platform].enabled ? 'border-primary bg-primary/10 text-primary' : 'border-border text-foreground'
                        }`}
                      >
                        {selectedPlatforms[platform].enabled && <FiCheck size={16} />}
                        <span className="font-semibold capitalize">{platform}</span>
                      </button>
                      {selectedPlatforms[platform].enabled && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {platformOptions[platform].map(type => {
                            const isEnabled = enabledContentTypes.includes(type);
                            const isSelected = selectedPlatforms[platform].types.includes(type);
                            return (
                              <button
                                key={type}
                                onClick={() => isEnabled && toggleContentType(platform, type)}
                                disabled={!isEnabled}
                                className={`px-3 py-1 rounded-lg border-2 text-sm transition-all ${
                                  !isEnabled 
                                    ? 'border-border/50 text-muted-foreground/50 cursor-not-allowed opacity-60' 
                                    : isSelected 
                                      ? 'border-purple-500 bg-purple-500/20 text-purple-300' 
                                      : 'border-border text-muted-foreground hover:border-primary/50'
                                }`}
                                title={!isEnabled ? 'Coming Soon' : ''}
                              >
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                                {!isEnabled && <span className="ml-1 text-xs opacity-70">Soon</span>}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Coming Soon Note */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm text-blue-300">
                    <strong>✨ Coming Soon:</strong> Stories, Reels, Carousels, Videos and more content types are on the way! Currently, Posts are available for all platforms.
                  </div>

                  {/* Dynamic Image Options - Different for photo vs no-photo */}
                  <div className="pt-4">
                    <label className="block text-sm font-medium text-foreground mb-1">
                      How many images per idea?
                    </label>
                    {imagesPerIdea === null && (
                      <p className="text-xs text-amber-400 mb-3">Please select an option below</p>
                    )}
                    <div className="space-y-2">
                      {/* Option 1: Single image */}
                      <button
                        onClick={() => { setImagesPerIdea(1); setGenerateIndividualImages(true); }}
                        className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                          imagesPerIdea === 1 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium text-foreground">1 image</span>
                            <span className="text-muted-foreground text-sm ml-2">— {photoAnalysis ? 'Hero shot' : 'Single visual'}</span>
                          </div>
                        </div>
                      </button>
                      
                      {/* Option 2: Multiple images (recommended) */}
                      <button
                        onClick={() => { setImagesPerIdea(getDetectedAngles()); setGenerateIndividualImages(true); }}
                        className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                          imagesPerIdea === getDetectedAngles() 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium text-foreground">{getDetectedAngles()} images</span>
                            <span className="text-muted-foreground text-sm ml-2">
                              — {photoAnalysis ? 'All unique angles' : 'Style variations'}
                            </span>
                          </div>
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full font-medium">
                            Recommended
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {photoAnalysis 
                            ? `${getDetectedAngles()} distinct angles detected in your photo`
                            : `${getDetectedAngles()} unique visual interpretations`
                          }
                        </p>
                      </button>
                      
                      {/* Option 3: More images */}
                      <button
                        onClick={() => { setImagesPerIdea(getDetectedAngles() * 2); setGenerateIndividualImages(true); }}
                        className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                          imagesPerIdea === getDetectedAngles() * 2 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium text-foreground">{getDetectedAngles() * 2} images</span>
                            <span className="text-muted-foreground text-sm ml-2">
                              — {photoAnalysis ? 'Angles + variations' : 'Extended variations'}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {photoAnalysis 
                            ? `${getDetectedAngles()} angles × 2 style variations each`
                            : `${getDetectedAngles() * 2} diverse visual styles`
                          }
                        </p>
                      </button>
                      
                      {/* Option 4: Custom amount (only for photo mode) */}
                      {photoAnalysis && (
                        <div className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                          imagesPerIdea && ![1, getDetectedAngles(), getDetectedAngles() * 2].includes(imagesPerIdea)
                            ? 'border-primary bg-primary/10' 
                            : 'border-border'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <span className="font-medium text-foreground">Custom amount</span>
                              <span className="text-muted-foreground text-sm ml-2">— More context variations</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <input
                              type="number"
                              min={getDetectedAngles() + 1}
                              max="10"
                              placeholder={`${getDetectedAngles() + 1}-10`}
                              value={imagesPerIdea && ![1, getDetectedAngles(), getDetectedAngles() * 2].includes(imagesPerIdea) ? imagesPerIdea : ''}
                              onChange={(e) => {
                                const val = parseInt(e.target.value);
                                if (!isNaN(val) && val >= 1 && val <= 10) {
                                  setImagesPerIdea(val);
                                  setGenerateIndividualImages(true);
                                }
                              }}
                              className="w-20 px-3 py-2 bg-secondary border border-border rounded-lg text-center text-foreground focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                            />
                            <span className="text-sm text-muted-foreground">images</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            <span className="text-green-400">✓ Product preserved exactly</span> — backgrounds & contexts reimagined
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Image Options */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Image Ratio</label>
                      <div className="flex flex-wrap gap-2">
                        {IMAGE_RATIOS.map(r => (
                          <button
                            key={r.id}
                            onClick={() => setImageRatio(r.id)}
                            className={`px-3 py-2 rounded-lg border-2 text-sm ${imageRatio === r.id ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}
                          >
                            {r.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Image Quality</label>
                      <div className="flex flex-wrap gap-2">
                        {IMAGE_QUALITY.map(q => (
                          <button
                            key={q.id}
                            onClick={() => setImageQuality(q.id)}
                            className={`px-3 py-2 rounded-lg border-2 text-sm ${imageQuality === q.id ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}
                          >
                            {q.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-t border-border bg-card rounded-b-xl">
          <button onClick={onClose} className="px-6 py-2 text-foreground hover:text-foreground">Cancel</button>
          
          {/* Step 1: Photo + Context Selection */}
          {step === 1 && (
            <div className="flex items-center space-x-3">
              <button
                onClick={handleProceedToFocus}
                disabled={analyzingPhoto || (uploadedPhoto && !contextMode)}
                className="flex items-center space-x-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 font-medium disabled:opacity-50"
                data-testid="continue-to-focus-btn"
              >
                {analyzingPhoto ? (
                  <>
                    <FiLoader className="animate-spin" />
                    <span>Analyzing...</span>
                  </>
                ) : photoAnalysis ? (
                  <>
                    <FiCheck />
                    <span>Continue with Analysis</span>
                  </>
                ) : uploadedPhoto && contextMode ? (
                  <>
                    <FiZap />
                    <span>Analyze &amp; Continue</span>
                  </>
                ) : !uploadedPhoto ? (
                  <>
                    <FiSkipForward />
                    <span>Skip - No Photo</span>
                  </>
                ) : (
                  <>
                    <FiInfo />
                    <span>Select Photo Type</span>
                  </>
                )}
              </button>
            </div>
          )}
          
          {/* Step 2: Focus Selection - no button needed, user clicks on option */}
          {step === 2 && (
            <button
              onClick={() => setStep(1)}
              className="flex items-center space-x-2 px-6 py-3 border border-border text-foreground rounded-lg hover:bg-secondary"
            >
              <span>← Change Photo</span>
            </button>
          )}
          
          {/* Step 3: Generate */}
          {step === 3 && (
            <button
              onClick={handleGenerateContent}
              disabled={getTotalIdeas() === 0 || loading || hasInsufficientCredits() || imagesPerIdea === null}
              className="flex items-center space-x-2 px-6 py-3 rounded-lg font-medium bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              data-testid="generate-content-btn"
            >
              {loading ? (
                <>
                  <FiLoader className="animate-spin" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <FiZap />
                  <span>Generate {getTotalIdeas()} {getTotalIdeas() === 1 ? 'Idea' : 'Ideas'}</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default ContentFocusModal;
